import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { PerformanceMonitor, AdaptiveDpr } from '@react-three/drei';
import CameraRig from './CameraRig';
import PostEffects from './PostEffects';
import SupernovaBackground from './environment/SupernovaBackground';
import Starfield from './environment/Starfield';
import CosmicFog from './environment/CosmicFog';
import AmbientLighting from './environment/AmbientLighting';
import DistantMoons from './environment/DistantMoons';
import NebulaField from './environment/NebulaField';
import DeepCosmos from './environment/DeepCosmos';
import SolarPlanet from './planets/SolarPlanet';
import Gateway from './objects/Gateway';
import SpaceTransition from './objects/SpaceTransition';
import DiscoveryNebula from './objects/DiscoveryNebula';
import MobileOptimizer from './MobileOptimizer';

export default function SpaceCanvas() {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh', zIndex: 1 }}>
      <Canvas
        dpr={[1, 1.5]}
        gl={{ antialias: false, powerPreference: 'high-performance', stencil: false, alpha: false }}
        camera={{ position: [0, 0, 80], fov: 45, near: 0.1, far: 800 }}
      >
        <PerformanceMonitor />
        <AdaptiveDpr pixelated />
        <MobileOptimizer />

        <Suspense fallback={null}>
          <CameraRig />

          {/* 1. Full 360° Dense Supernova Background Dome & Volumetric Gas Fields */}
          <SupernovaBackground />

          {/* 2. Starfield & Coherent Meteor Stream */}
          <Starfield />
          <NebulaField />
          <DeepCosmos />
          <DistantMoons />
          <CosmicFog />
          <AmbientLighting />

          {/* 1. Earth-like Habitable World (Waymaker) */}
          <SolarPlanet
            type="earth"
            radius={4.2}
            position={[5.5, 0, 50]}
            hasClouds={true}
            atmosphereColor="#60a5fa"
            rotationSpeed={0.06}
          />

          {/* 2. Mars Desert Foundry World (Pulse Engine) */}
          <SolarPlanet
            type="mars"
            radius={3.6}
            position={[-5.5, 0.5, 15]}
            atmosphereColor="#fb923c"
            rotationSpeed={0.05}
          />

          {/* Space Transition Asteroid Belt */}
          <SpaceTransition position={[0, 0, 0]} />

          {/* 3. Saturn / Jupiter Banded Ring Giant (Celestial Drift) */}
          <SolarPlanet
            type="jupiter"
            radius={5.2}
            position={[6.5, -0.5, -20]}
            hasRings={true}
            atmosphereColor="#fde047"
            rotationSpeed={0.07}
            axialTilt={0.44}
          />

          {/* 4. Neptune Ice Crystal Giant (FormCraft) */}
          <SolarPlanet
            type="neptune"
            radius={4.0}
            position={[-5.5, 0.5, -55]}
            atmosphereColor="#38bdf8"
            rotationSpeed={0.05}
          />

          {/* Atmospheric Discovery Nebula Cloud */}
          <DiscoveryNebula position={[0, 0, -72]} />

          {/* 5. Volcanic Core World (Ember Chronicles) */}
          <SolarPlanet
            type="volcanic"
            radius={3.8}
            position={[5.5, 0, -90]}
            atmosphereColor="#ef4444"
            rotationSpeed={0.04}
          />

          {/* 6. Cybernetic Dyson Nexus World (Code Forge AI) */}
          <SolarPlanet
            type="cyber"
            radius={4.2}
            position={[-5.5, 0.5, -125]}
            atmosphereColor="#c084fc"
            rotationSpeed={0.08}
          />

          {/* 7. Final Solar Gateway Portal (Positioned at z = -165) */}
          <Gateway position={[0, 0, -165]} />

          {/* Cinematic Post-Processing */}
          <PostEffects />
        </Suspense>
      </Canvas>
    </div>
  );
}
